import { Component, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../../../@shared/components/table-component/table-component';
import { Params } from '../../../../../services/utilis/add-params-service';
import { FormControl } from '@angular/forms';
import { enumToOptions } from '../../../../../@shared/utility/enumToOptions';
import { CarStatus } from '../../../../../models/enums';
import { ActivatedRoute, Router } from '@angular/router';
import { CarActivityService } from '../../../../../services/vehicles/car-activity-service';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { CarActivity } from '../../../../../models/car-activity';
import { PaginatorState } from 'primeng/paginator';
import { Card } from 'primeng/card';

@Component({
  selector: 'app-car-details-activity-component',
  imports: [TableComponent, Card],
  templateUrl: './car-details-activity-component.html',
  styleUrl: './car-details-activity-component.scss'
})
export class CarDetailsActivityComponent implements OnInit {

  @ViewChild(TableComponent) table!: TableComponent<CarActivity>;

  @Input() carID: string | undefined;

  allFilter: CustomFilter = {
    key: "all",
    label: "All",
    type: "chip",
    options: [{ label: 'All', value: null, clearsOthers: true }]
  }

  carStatusFilter: CustomFilter = {
    key: "carStatus",
    label: "Status",
    type: "multiselect",
    options: enumToOptions(CarStatus)
  }

  commandStatusFilter: CustomFilter = {
    key: "commandStatus",
    label: "Command Status",
    type: "select",
    options: [
      { label: 'Success', value: 'SUCCESS' },
      { label: 'Fail', value: 'FAIL' },
    ]
  }

  createdDateFilter: CustomFilter = {
    key: "created",
    label: "Date",
    type: "date-range",
    options: []
  }

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'carStatus',
    'commandStatus',
    'createdFrom',
    'createdTo'
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    carStatus: "carStatus",
    commandStatus: "commandStatus",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }


  tableColumns: DynamicColumn<CarActivity>[] = [
    { header: 'Email', field: 'email', sortable: false, },
    { header: 'Command name', field: 'commandName', sortable: false, },
    {
      header: 'Status',
      field: 'carStatus',
      sortable: false,
      isStatusBadge: true,
      badgeStyleGetter: (value) => {
        const map: Record<string, { background: string; color: string }> = {
          'AVAILABLE': { background: 'var(--status-available-bg)', color: 'var(--status-available-text)' },
          'IN_USE': { background: 'var(--status-in-use-bg)', color: 'var(--status-in-use-text)' },
          'RESERVED': { background: 'var(--status-reserved-bg)', color: 'var(--status-reserved-text)' },
          'MAINTENANCE': { background: 'var(--status-maintenance-bg)', color: 'var(--status-maintenance-text)' },
          'DISCHARGED': { background: 'var(--status-discharged-bg)', color: 'var(--status-discharged-text)' },
          'REBALANCING': { background: 'var(--status-rebalancing-bg)', color: 'var(--status-rebalancing-text)' },
          'NEEDS_INVESTIGATION': { background: 'var(--status-investigate-bg)', color: 'var(--status-investigate-text)' },
          'TRANSPORTATION': { background: 'var(--status-transportation-bg)', color: 'var(--status-transportation-text)' },
          'NOT_READY': { background: 'var(--status-not-ready-bg)', color: 'var(--status-not-ready-text)' },
        };
        return map[value as string] ?? { background: 'var(--status-reserved-bg)', color: 'var(--status-reserved-text)' };
      }
    },
    {
      header: 'Created At',
      field: 'createdAt',
      sortable: false,
      isDateTime: true
    },

  ]

  activitiesList = signal<CarActivity[]>([])
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  searchControl = new FormControl('');

  private carActivityService = inject(CarActivityService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  ngOnInit() {
    this.getCarActivityList()
  }

  getCarActivityList = () => {
    this.activatedRoute.queryParams.pipe(

      // ✅ ONLY run when tab = 1
      filter(params => params['tab'] === '1'),

      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((prev, curr) =>
        JSON.stringify(prev) === JSON.stringify(curr)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),

      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'createdAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.carActivityService.getCarActivityList(this.carID!, params);
      })

    )
      .subscribe((res) => {
        this.activitiesList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  onSearchChange = () => {
    this.searchControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged()
    ).subscribe((value) => {
      if (value == "") {
        value = ''
      }
      this.router.navigate([], {
        queryParams: {
          search: value,
          page: undefined,
          size: undefined,
        },
        queryParamsHandling: "merge"
      })
    })
  }

  resetAll = (tab: string) => {
    this.table?.clearAllFilters();

    this.router.navigate([], {
      queryParams: {
        tab: tab,
        search: null,
        page: null,
        size: null,
        carStatus: undefined,
        commandStatus: undefined,
        createdFrom: null,
        createdTo: null,
      },
      queryParamsHandling: ''
    });
  }
}
